import { useState } from "react";
import Text, { TextVariant } from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import { simpleDate, toDateOnlyISO, toLocalDate } from "@/utils/datetime";
import DateRangePicker, {
  DateRange
} from "@/components/controls/DateRangePicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Close } from "@dreamhub/designsystem/icons";
import { Calendar as CalendarIcon } from "lucide-react";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import { ensureDate } from "@/utils/dateRange";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";

interface DateRangeFieldProps {
  value?: {
    fromDate: string;
    toDate: string;
  };
  onUpdate: (value: { fromDate: string; toDate: string }) => void;
  fieldKey: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  placeholderVariant?: TextVariant;
  alwaysShowIcon?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  variant?: "form" | "inline";
  readOnly?: boolean;
  onClear?: () => void;
}

const DateRangeField: React.FC<DateRangeFieldProps> = ({
  value,
  onUpdate,
  fieldKey,
  className,
  placeholder,
  disabled = false,
  placeholderVariant,
  alwaysShowIcon = false,
  isOpen: externalIsOpen,
  onOpenChange: externalOnOpenChange,
  variant = "form",
  readOnly = false,
  onClear
}) => {
  const { t } = useTranslation();
  const isNewFieldsUI = useNewFieldsUI();
  // The redesign drops the placeholder a step in the hierarchy; before it, the
  // fallback was one step up.
  const resolvedPlaceholderVariant =
    placeholderVariant ?? (isNewFieldsUI ? "bodyTertiary" : "bodySecondary");
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isDateRangeOpen =
    externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsDateRangeOpen = externalOnOpenChange || setInternalIsOpen;

  const handleDateRangeUpdate = ({ range }: { range: DateRange }) => {
    const fromDate = ensureDate(range.fromDate);
    const toDate = ensureDate(range.toDate);

    if (!fromDate) {
      console.error("Invalid fromDate in handleDateRangeUpdate");
      return;
    }

    const dateRangeValue = {
      fromDate: toDateOnlyISO(fromDate),
      toDate: toDateOnlyISO(toDate || fromDate)
    };
    onUpdate(dateRangeValue);
    setIsDateRangeOpen(false);
  };

  const handleDateRangeCancel = () => {
    setIsDateRangeOpen(false);
  };

  const formatDateRange = (dateRange: DateRangeFieldProps["value"]) => {
    if (!dateRange?.fromDate)
      return (
        placeholder || (isNewFieldsUI ? t("select") : t("selectDateRange"))
      );

    const fromDate = toLocalDate(dateRange.fromDate);
    const toDate = dateRange.toDate ? toLocalDate(dateRange.toDate) : fromDate;

    if (fromDate.getTime() === toDate.getTime()) {
      return simpleDate(fromDate);
    }

    return `${simpleDate(fromDate)} - ${simpleDate(toDate)}`;
  };

  if (readOnly) {
    return (
      <div className="px-2 py-2 min-h-9 flex items-center">
        <Text variant={value?.fromDate ? "body" : resolvedPlaceholderVariant}>
          {formatDateRange(value)}
        </Text>
      </div>
    );
  }

  const renderLegacyTrigger = () => {
    const displayValue = formatDateRange(value);
    const hasValue = value?.fromDate;

    const triggerClasses =
      variant === "form"
        ? cn(
            "w-full pl-3 pr-2 py-[6px] rounded-lg min-h-9 flex items-center justify-between cursor-pointer transition-all duration-200 ease-in-out bg-surface-field-default border-[0.5px] border-border-field-default hover:border-border-field-hover",
            isDateRangeOpen &&
              "border-[1px] border-primary bg-surface-focus-selected",
            disabled && "cursor-not-allowed opacity-50",
            className
          )
        : cn(
            "w-full flex items-center justify-between cursor-pointer transition-colors",
            isDateRangeOpen && "border-primary",
            disabled && "cursor-not-allowed opacity-50",
            className
          );

    return (
      <div
        className={cn(
          "relative group",
          variant === "form" &&
            !disabled &&
            (isDateRangeOpen ? "shadow-field-focus" : "shadow-field")
        )}
      >
        <div className={triggerClasses} data-test={`${fieldKey}-input`}>
          <Text
            as="span"
            variant={hasValue ? "body" : resolvedPlaceholderVariant}
            className="truncate h-full flex items-center"
          >
            {displayValue}
          </Text>
          <IconContainer
            color="secondary"
            className={cn(
              "flex-shrink-0 ml-2 transition-all duration-150 opacity-0 group-hover:opacity-100",
              alwaysShowIcon && "opacity-100"
            )}
          >
            <CalendarIcon />
          </IconContainer>
        </div>
      </div>
    );
  };

  const renderNewTrigger = () => {
    const displayValue = formatDateRange(value);
    const hasValue = value?.fromDate;

    const triggerClasses =
      variant === "form"
        ? cn(
            "w-full pl-3 pr-2 py-[6px] rounded-lg min-h-9 flex items-center justify-between cursor-pointer transition-all duration-200 ease-in-out bg-surface-field-default border-[0.5px] shadow-field",
            className,
            isDateRangeOpen
              ? "border-border-field-focus bg-surface-focus-selected shadow-field-focus"
              : "border-border-field-default hover:border-border-field-hover",
            disabled && "cursor-not-allowed opacity-50 shadow-none"
          )
        : cn(
            "px-2 py-1 rounded-lg w-full min-h-9 flex items-center justify-between transition-all duration-200 ease-in-out",
            isDateRangeOpen
              ? "border-[0.5px] border-border-field-focus bg-surface-focus-selected cursor-pointer"
              : "border-[0.5px] border-transparent",
            !isDateRangeOpen &&
              !disabled &&
              "hover:bg-surface-hover-soft cursor-pointer",
            // Matches the Select family's inline disabled look: no fill, no
            // hover. DateSelection already does this; the stories state the two
            // families share the variant name and so share its disabled look.
            disabled && "cursor-default",
            className
          );

    return (
      <div className="relative group w-full">
        <div className={triggerClasses} data-test={`${fieldKey}-input`}>
          <Text
            as="span"
            variant={hasValue ? "body" : resolvedPlaceholderVariant}
            className={cn(
              "truncate h-full flex items-center flex-1",
              disabled && "!text-body-disabled"
            )}
          >
            {displayValue}
          </Text>
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            {/* !disabled, like DateSelection.canClear and DateTimeField's
                showClear: `isDateRangeOpen` can be driven from outside and
                `disabled` can flip while it stays true, leaving a
                permission-locked range clearable. */}
            {hasValue && isDateRangeOpen && onClear && !disabled && (
              <IconButton
                size="xs"
                variant="iconNoBg"
                type="button"
                aria-label={t("common.clear")}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Close />
              </IconButton>
            )}
            {!isDateRangeOpen && !disabled && (
              <IconContainer
                color={variant === "form" ? "secondary" : "tertiary"}
                size="5"
                className={cn(
                  "flex-shrink-0 transition-all duration-150",
                  variant !== "form" &&
                    !alwaysShowIcon &&
                    "opacity-0 group-hover:opacity-100"
                )}
              >
                <CalendarIcon size={20} strokeWidth={1.5} />
              </IconContainer>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTrigger = () =>
    isNewFieldsUI ? renderNewTrigger() : renderLegacyTrigger();

  // `asChild` hands the trigger role to the rendered div, and `disabled` on a
  // div is inert — it suppresses no pointer events, so the click still reached
  // Radix's open toggle and a permission-locked range opened its picker and
  // wrote through onUpdate. Same mechanism, and same fix, as DateSelection's.
  // Ungated deliberately: `asChild` is not part of the redesign here — base
  // passes it too — so gating the guard would leave the write live with the
  // flag off. Close is never blocked, only open.
  const handleOpenChange = (open: boolean) => {
    if (disabled && open) return;
    setIsDateRangeOpen(open);
  };

  return (
    <Popover open={isDateRangeOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {renderTrigger()}
      </PopoverTrigger>
      <PopoverContent
        padding={0}
        className="w-fit"
        align="start"
        sideOffset={4}
      >
        <DateRangePicker
          labels={{ cancel: t("cancel"), apply: t("apply") }}
          initialFromDate={
            value?.fromDate ? toLocalDate(value.fromDate) : undefined
          }
          initialToDate={value?.toDate ? toLocalDate(value.toDate) : undefined}
          onUpdate={handleDateRangeUpdate}
          onCancel={handleDateRangeCancel}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateRangeField;
